/** Start screen with lake illustration and the two-step permission flow buttons. */
import type { UiCallbacks } from '../contracts';
import { APP_VERSION_LABEL } from '../settings';
import { button, fromHtml, guardXrSelect, h, setClass, setHidden } from './dom';
import { icon } from './icons';
import { lakeArtSvg } from './lake-art';

export type StartPhase = 'idle' | 'locating' | 'ready-ar' | 'starting' | 'error';

const DEFAULT_MESSAGES: Record<StartPhase, string> = {
  idle: '',
  locating: 'Standort wird ermittelt …',
  'ready-ar': 'Standort bereit. Jetzt die Kamera starten.',
  starting: 'AR-Kamera wird gestartet …',
  error: 'Start fehlgeschlagen.',
};

export class StartScreen {
  readonly el: HTMLElement;
  private readonly startBtn: HTMLButtonElement;
  private readonly arBtn: HTMLButtonElement;
  private readonly status: HTMLElement;
  private readonly statusText: HTMLElement;
  private readonly spinner: HTMLElement;
  phase: StartPhase = 'idle';

  constructor(callbacks: UiCallbacks) {
    this.startBtn = button('uz-btn uz-btn--primary uz-start-cta', [icon('play'), h('span', null, null, ['App starten'])], () => {
      if (this.phase === 'idle' || this.phase === 'error') callbacks.onStart();
    });
    this.arBtn = button('uz-btn uz-btn--primary uz-start-cta', [icon('camera'), 'AR-Kamera starten'], () => {
      if (this.phase === 'ready-ar') callbacks.onAction('start-ar');
    }, { hidden: true });
    this.spinner = h('span', 'uz-spinner', { 'aria-hidden': 'true' });
    this.statusText = h('span');
    this.status = h('div', 'uz-start-status', { role: 'status', 'aria-live': 'polite' }, [this.spinner, this.statusText]);

    const art = h('div', 'uz-start-art');
    art.append(fromHtml(lakeArtSvg()));

    const body = h('div', 'uz-start-body', null, [
      h('h1', 'uz-start-title', null, ['Uferzone ', h('span', null, null, ['AR'])]),
      h('p', 'uz-start-sub', null, ['Innere und äussere Uferzone live im Kamerabild der Luzerner Seen.']),
      this.startBtn,
      this.arBtn,
      this.status,
    ]);
    const foot = h('footer', 'uz-start-foot', null, [
      h('strong', null, null, ['Orientierungshilfe, keine amtliche Navigationsgrundlage.']),
      h('span', null, null, [APP_VERSION_LABEL]),
    ]);
    this.el = h('section', 'uz-start', { 'aria-label': 'Uferzone AR Start' }, [art, body, foot]);
    guardXrSelect(this.el);
    this.setPhase('idle');
  }

  dispose(): void {}

  setPhase(phase: StartPhase, message?: string): void {
    this.phase = phase;
    const text = message ?? DEFAULT_MESSAGES[phase];
    const busy = phase === 'locating' || phase === 'starting';
    setHidden(this.startBtn, phase === 'ready-ar' || phase === 'starting');
    setHidden(this.arBtn, !(phase === 'ready-ar' || phase === 'starting'));
    this.startBtn.disabled = busy;
    this.arBtn.disabled = phase === 'starting';
    const label = this.startBtn.querySelector('span');
    if (label) label.textContent = phase === 'error' ? 'Erneut versuchen' : 'App starten';
    setHidden(this.spinner, !busy);
    this.statusText.textContent = text;
    setHidden(this.status, !text);
    setClass(this.status, 'is-error', phase === 'error');
    this.el.setAttribute('aria-busy', String(busy));
  }

  /** Focus the relevant primary button (keyboard users, after phase changes). */
  focusPrimary(): void {
    (this.arBtn.hidden ? this.startBtn : this.arBtn).focus({ preventScroll: true });
  }
}
